import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authService } from '../services/authService';
import AuthLayout from '../components/public/AuthLayout';
import TextField from '../components/public/TextField';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await authService.forgotPassword({ email });
      setSubmitted(true);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
        ?? 'Unable to submit request. Try again later.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Forgot your password?"
      subtitle="We'll send instructions to reset it. For security, the same message appears whether or not the address has an account."
      backHref="/login"
      backLabel="Back to sign in"
    >
      {submitted ? (
        <div className="space-y-5">
          <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-lg px-4 py-3">
            If an account exists for that address, you'll receive a reset link within a few minutes. Links expire in 60 minutes.
          </div>
          <Link
            to="/login"
            className="block w-full text-center bg-[var(--primary)] text-white py-3 rounded-lg text-sm font-semibold hover:bg-[var(--primary-light)] transition-colors"
          >
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3">
              {error}
            </div>
          )}
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            autoFocus
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <button
            type="submit"
            disabled={loading || !email}
            className="w-full bg-[var(--primary)] text-white py-3 rounded-lg text-sm font-semibold hover:bg-[var(--primary-light)] disabled:opacity-50 transition-colors"
          >
            {loading ? 'Submitting…' : 'Send reset instructions'}
          </button>
        </form>
      )}
    </AuthLayout>
  );
};

export default ForgotPassword;
